<template>
  <div class="w-full">
    <!-- Main Navbar -->
    <div class="navbar bg-base-100/95 dark:bg-neutral/95 backdrop-blur-sm text-base-content dark:text-neutral-content shadow-md fixed top-0 z-[100] transition-all duration-300" :class="{'bg-base-100 dark:bg-neutral': scrolled}">
      <div class="container mx-auto">
        <!-- Mobile Nav & Sidebar Button -->
        <div class="flex-none lg:hidden">
          <label for="drawer-toggle" class="btn btn-square btn-ghost drawer-button">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-6 h-6 stroke-current">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </label>
        </div>
        
        <!-- Logo Section -->
        <div class="flex-none lg:flex-shrink-0">
          <router-link to="/" class="flex items-center gap-2 transition-transform hover:scale-105 duration-300">
            <img src="/logo.png" alt="fastpano Logo" class="h-10 w-10 object-contain" />
            <span class="text-lg font-bold">fastPano.com</span>
          </router-link>
        </div>

        <!-- Desktop Navigation Links -->
        <div class="hidden lg:flex flex-1 min-w-0 justify-end items-center gap-2">
          <ul class="menu menu-horizontal px-1 gap-0.5 flex-nowrap flex-1 justify-end">
            <li v-for="item in menuItems" :key="item.id" class="whitespace-nowrap flex-shrink-0">
              <a 
                :href="`#${item.id}`" 
                @click.prevent="scrollToSection(item.id)" 
                class="rounded-lg font-medium group text-sm px-2.5 py-2" 
                :class="{'active': activeSection === item.id}"
              >
                <span class="relative">
                  {{ item.label }}
                  <span class="absolute bottom-0 left-0 w-0 group-hover:w-full h-0.5 bg-primary transition-all duration-300"></span>
                </span>
              </a>
            </li>
          </ul>
            <a 
            href="https://www.instagram.com/fastpano" 
            target="_blank" 
            rel="noopener noreferrer"
            class="btn btn-ghost btn-circle btn-sm hover:bg-primary/20 transition-all duration-300"
            :title="t.header.followInstagram"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="fill-current">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
        </div>
          </div>
          </div>

    <!-- Mobile Drawer -->
    <div class="drawer drawer-end lg:hidden">
      <input id="drawer-toggle" type="checkbox" class="drawer-toggle" v-model="drawerOpen" />
      <div class="drawer-side z-[110]">
        <label for="drawer-toggle" class="drawer-overlay z-[105]"></label>
        <div class="w-80 min-h-full bg-base-100 text-base-content z-[110] relative flex flex-col">
          <!-- Drawer Header -->
          <div class="p-6 border-b border-base-300">
            <div class="flex items-center gap-3">
              <img src="/logo.png" alt="fastpano Logo" class="h-10 w-10 object-contain" />
              <div>
                <div class="text-xl font-bold">fastPano</div>
                <div class="text-xs opacity-60">{{ t.header.subtitle }}</div>
              </div>
            </div>
          </div>
          
          <!-- Menu Items -->
          <div class="flex-1 p-4 space-y-2 overflow-y-auto">
            <a 
              v-for="item in menuItems" 
              :key="item.id"
              :href="`#${item.id}`" 
              @click.prevent="handleMobileMenuClick(item.id)" 
              class="block px-4 py-4 rounded-xl font-semibold text-base transition-all duration-300 group relative overflow-hidden"
              :class="{
                'bg-primary text-primary-content shadow-lg scale-[1.02]': activeSection === item.id,
                'bg-base-200 dark:bg-base-300 text-base-content hover:bg-base-300 dark:hover:bg-base-400 hover:shadow-md': activeSection !== item.id
              }"
            >
              <!-- Active indicator -->
              <div 
                v-if="activeSection === item.id"
                class="absolute left-0 top-0 bottom-0 w-1 bg-primary-content rounded-r-full"
              ></div>
              
              <!-- Menu item content -->
              <div class="flex items-center justify-between relative z-10">
                <span class="flex-1">{{ item.label }}</span>
                <svg 
                  v-if="activeSection === item.id"
                  xmlns="http://www.w3.org/2000/svg" 
                  class="h-5 w-5 ml-2" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
                <svg 
                  v-else
                  xmlns="http://www.w3.org/2000/svg" 
                  class="h-5 w-5 ml-2 opacity-40 group-hover:opacity-70 transition-opacity" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              
              <!-- Hover effect -->
              <div 
                v-if="activeSection !== item.id"
                class="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:via-primary/10 group-hover:to-primary/5 transition-all duration-300 rounded-xl"
              ></div>
            </a>
          </div>
          
          <!-- Drawer Footer -->
          <div class="p-4 border-t border-base-300">
            <div class="flex justify-center mb-3">
              <a 
                href="https://www.instagram.com/fastpano" 
                target="_blank" 
                rel="noopener noreferrer"
                class="btn btn-circle btn-ghost btn-sm hover:bg-primary/20 transition-all duration-300"
                :title="t.header.followInstagram"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="fill-current">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
            <div class="text-xs text-center opacity-60">
              {{ t.header.copyright }} © {{ new Date().getFullYear() }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Spacer to prevent content from being hidden under fixed header -->
    <div class="h-16"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from '../../compositions/useI18n';

const { t } = useI18n();

// Menu items in section order
const menuItems = computed(() => [
  { id: 'features', label: t.value.header.menu.features, icon: '✨' },
  { id: 'ozet', label: t.value.header.menu.summary, icon: '📋' },
  { id: 'how-it-works', label: t.value.header.menu.howItWorks, icon: '⚙️' },
  { id: 'benefits', label: t.value.header.menu.benefits, icon: '🎯' },
  { id: 'media-types', label: t.value.header.menu.mediaTypes, icon: '📱' },
  { id: 'category-slideshow', label: t.value.header.menu.categorySlideshow, icon: '🎨' },
  { id: 'faq', label: t.value.header.menu.faq, icon: '❓' },
  { id: 'download', label: t.value.header.menu.download, icon: '📥' },
]);

// Scroll tracking
const scrolled = ref(false);
const activeSection = ref('hero');
const drawerOpen = ref(false);

// Scroll to section
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId) || document.querySelector(`[data-section="${sectionId}"]`);
  if (element) {
    const offset = 80; // Header height
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

// Handle mobile menu click
const handleMobileMenuClick = (sectionId) => {
  scrollToSection(sectionId);
  // Close drawer after a short delay to allow smooth scroll
  setTimeout(() => {
    drawerOpen.value = false;
  }, 300);
};

// Track active section on scroll
const handleScroll = () => {
  scrolled.value = window.scrollY > 50;
  
  const sections = [
    { id: 'hero', element: document.getElementById('hero') },
    { id: 'features', element: document.getElementById('features') },
    { id: 'ozet', element: document.getElementById('ozet') },
    { id: 'how-it-works', element: document.getElementById('how-it-works') },
    { id: 'benefits', element: document.getElementById('benefits') },
    { id: 'media-types', element: document.getElementById('media-types') },
    { id: 'category-slideshow', element: document.getElementById('category-slideshow') },
    { id: 'faq', element: document.getElementById('faq') },
    { id: 'download', element: document.getElementById('download') },
  ];

  const scrollPosition = window.scrollY + 100;

  for (let i = sections.length - 1; i >= 0; i--) {
    const section = sections[i];
    if (section.element) {
      const sectionTop = section.element.offsetTop;
      const sectionHeight = section.element.offsetHeight;
      
      if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
        activeSection.value = section.id;
        break;
      }
    }
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  handleScroll(); // Initial check
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
.dropdown-content {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .navbar {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

.animate-slideDown {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Active navigation link */
.menu a.active {
  color: hsl(var(--p));
  background-color: hsl(var(--p) / 0.1);
}

.menu a.active .relative span:last-child {
  width: 100%;
}

/* Prevent menu from wrapping */
.menu.menu-horizontal {
  flex-wrap: nowrap !important;
}

.menu.menu-horizontal li {
  flex-shrink: 0;
}

/* Mobile drawer animations */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.drawer-side > div {
  animation: slideInRight 0.3s ease-out;
}

/* Z-index fixes */
.navbar {
  z-index: 100;
}

.drawer-side {
  z-index: 110 !important;
}

.drawer-overlay {
  z-index: 105 !important;
}
</style>
